import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";

/**
 * Iter 134 — Dashboard Contabilità.
 * Vista dedicata per `accounting_operator` (e `admin`) con:
 *  - Elenco pratiche in `status=awaiting_payment`
 *  - Totale importi in attesa
 *  - Tasto "Pagamento ricevuto" per sbloccare la singola pratica
 *
 * I `supervisor` non hanno accesso a questa pagina (403 dal backend).
 *
 * Iter140t: estratto il PANNELLO in un componente riusabile
 * `AwaitingPaymentsPanel`. La pagina di default include il chrome (h1,
 * tab wrapper) e ora è opzionale — il pannello viene renderizzato anche
 * come tab dentro `Accounting.jsx`.
 */
export function AwaitingPaymentsPanel({ showHeader = true }) {
  const { user } = useAuth();
  const [data, setData] = useState({ practices: [], count: 0, total_pending_eur: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/practices/awaiting-payment");
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nel caricamento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markReceived = async (pid) => {
    if (!window.confirm("Confermi di aver ricevuto il pagamento? La pratica sarà attivata.")) return;
    setBusy(pid);
    try {
      await api.post(`/practices/${pid}/mark-payment-received`);
      toast.success("Pagamento confermato — pratica attivata.");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore nell'aggiornamento");
    } finally { setBusy(null); }
  };

  const canMark = user?.role === "admin" || user?.role === "accounting_operator";
  const fmt = (v) => Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div className={showHeader ? "p-6 space-y-4" : "space-y-4"} data-testid="accounting-awaiting-page">
      {showHeader && (
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Contabilità · Pagamenti in attesa</h1>
            <p className="text-sm text-muted-foreground">
              Pratiche confermate con bonifico o metodo offline in attesa di riscontro sul conto corrente.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="accounting-refresh">Aggiorna</Button>
        </div>
      )}
      {!showHeader && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={load} data-testid="accounting-refresh">Aggiorna</Button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Card data-testid="accounting-count-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Pratiche in attesa</CardTitle></CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-indigo-700 tabular-nums">{data.count}</div>
          </CardContent>
        </Card>
        <Card data-testid="accounting-total-card">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Totale importi da incassare</CardTitle></CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-emerald-700 tabular-nums">€ {fmt(data.total_pending_eur)}</div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-10">Caricamento…</div>
      ) : data.practices.length === 0 ? (
        <Card><CardContent className="text-center text-muted-foreground py-8">Nessuna pratica in attesa di pagamento.</CardContent></Card>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm bg-white border rounded-md" data-testid="accounting-practices-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-3 py-2">Pratica</th>
                <th className="text-left px-3 py-2">Cliente</th>
                <th className="text-left px-3 py-2">Professionista</th>
                <th className="text-left px-3 py-2">Metodo</th>
                <th className="text-right px-3 py-2">Importo</th>
                <th className="text-center px-3 py-2">Azione</th>
              </tr>
            </thead>
            <tbody>
              {data.practices.map((p) => (
                <tr key={p._id} className="border-t" data-testid={`accounting-row-${p._id}`}>
                  <td className="px-3 py-2">
                    <Link to={`/practices/${p._id}`} className="text-indigo-700 hover:underline font-medium">
                      {p.service_name || "Servizio"}
                    </Link>
                    <div className="text-[11px] text-slate-500">{p.property_address || ""}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{p.client_name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{p.client_email || ""}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div>{p.professional_name || "—"}</div>
                    <div className="text-[11px] text-slate-500">{p.professional_email || ""}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className="inline-block px-2 py-0.5 rounded bg-amber-100 text-amber-900 text-[11px] font-semibold">
                      {p.payment_method_name || p.payment_method_type || "Bonifico"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">€ {fmt(p.total_price)}</td>
                  <td className="px-3 py-2 text-center">
                    {canMark ? (
                      <Button
                        size="sm"
                        onClick={() => markReceived(p._id)}
                        disabled={busy === p._id}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white border-0"
                        data-testid={`accounting-mark-${p._id}`}
                      >
                        {busy === p._id ? "…" : "✓ Ricevuto"}
                      </Button>
                    ) : (
                      <span className="text-[11px] text-slate-400 italic">Solo Admin/Accounting</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Iter140t: default export — pagina standalone (backward compat con la route
// `/accounting/awaiting-payments`). Redirect UX: da qui in poi la vista
// principale è dentro `/accounting` come tab "Pagamenti in attesa".
export default function AccountingAwaitingPayments() {
  return <AwaitingPaymentsPanel showHeader={true} />;
}
